import { useState, useEffect, useCallback, useRef } from 'react'
import type { QueueStatus, SubmissionEntry, JobTrackingApi } from '../types/jobTracking'

interface UseJobPollingOptions {
  entityId: string | undefined
  api: JobTrackingApi
  enabled?: boolean
  pollIntervalMs?: number
}

interface UseJobPollingResult {
  queueStatus: QueueStatus | null
  submissions: SubmissionEntry[]
  isLoadingQueue: boolean
  isLoadingSubmissions: boolean
  refreshQueue: () => Promise<void>
  refreshSubmissions: () => Promise<void>
  /** Track a specific submission (polls until completed/failed) */
  trackSubmission: (submissionId: string) => void
  trackedSubmission: SubmissionEntry | null
}

export function useJobPolling({
  entityId,
  api,
  enabled = true,
  pollIntervalMs = 10000,
}: UseJobPollingOptions): UseJobPollingResult {
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null)
  const [submissions, setSubmissions] = useState<SubmissionEntry[]>([])
  const [isLoadingQueue, setIsLoadingQueue] = useState(false)
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false)
  const [trackedId, setTrackedId] = useState<string | null>(null)
  const [trackedSubmission, setTrackedSubmission] = useState<SubmissionEntry | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval>>()

  const refreshQueue = useCallback(async () => {
    if (!entityId || !enabled) return
    setIsLoadingQueue(true)
    try {
      const status = await api.getQueueStatus(entityId)
      setQueueStatus(status)
    } catch {
      // silent
    } finally {
      setIsLoadingQueue(false)
    }
  }, [entityId, api, enabled])

  const refreshSubmissions = useCallback(async () => {
    if (!entityId || !enabled) return
    setIsLoadingSubmissions(true)
    try {
      const subs = await api.getSubmissions()
      setSubmissions(subs)
    } catch {
      // silent
    } finally {
      setIsLoadingSubmissions(false)
    }
  }, [entityId, api, enabled])

  const trackSubmission = useCallback((submissionId: string) => {
    setTrackedId(submissionId)
    setTrackedSubmission(null)
  }, [])

  // Poll queue + submissions
  useEffect(() => {
    if (!entityId || !enabled) return

    refreshQueue()
    refreshSubmissions()

    intervalRef.current = setInterval(() => {
      refreshQueue()
      refreshSubmissions()
    }, pollIntervalMs)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [entityId, enabled, pollIntervalMs, refreshQueue, refreshSubmissions])

  // Poll tracked submission until completed/failed
  useEffect(() => {
    if (!trackedId || !enabled) return

    const poll = async () => {
      try {
        const sub = await api.getSubmissionStatus(trackedId)
        if (sub) {
          setTrackedSubmission(sub)
          if (sub.status === 'completed' || sub.status === 'failed') {
            setTrackedId(null) // Stop polling
            refreshQueue()
            refreshSubmissions()
          }
        }
      } catch {
        // silent
      }
    }

    poll()
    const interval = setInterval(poll, 5000) // Poll faster for tracked job
    return () => clearInterval(interval)
  }, [trackedId, api, enabled, refreshQueue, refreshSubmissions])

  return {
    queueStatus,
    submissions,
    isLoadingQueue,
    isLoadingSubmissions,
    refreshQueue,
    refreshSubmissions,
    trackSubmission,
    trackedSubmission,
  }
}
